import { translate, useLanguage } from "@cqt/platform";
/**
 * Portal — the launchpad.
 *
 * Shows exactly the tools this user has been granted, and nothing else. The tile
 * list comes straight from /me, so it is a render of what the API already said
 * rather than a permission question asked again in the browser. A user with no
 * grants sees an explicit empty state, not a blank page: "nothing here" and
 * "something is broken" must not look the same.
 */
import { useEffect, useState } from "react";
import { AppShell, PageHeader, useMe, useAuth } from "@cqt/platform";
import type { GrantedTool, Level } from "@cqt/platform";
import { CompanyPicker } from "./CompanyPicker";
import { PeopleTab } from "./PeopleTab";
import { ProfileTab } from "./ProfileTab";
import { ToolIcon } from "./ToolIcon";
import { McpConnections } from "./McpConnections";

type Tab = "apps" | "profile" | "people" | "mcp";

const TABS: { key: Tab; label: string }[] = [
  { key: "apps", label: "Dashboard" },
  { key: "profile", label: "Profile" },
  { key: "people", label: "People" },
  { key: "mcp", label: "Connect your AI" },
];

/**
 * At the platform's own address there is no company, so there is no directory
 * and no company dashboard — only the person's own profile and the console.
 * Filtering here rather than rendering an empty People tab: "no company" and
 * "a company with nobody in it" must not look the same.
 */
function tabsFor(hasCompany: boolean) {
  return TABS.filter((tab) => (hasCompany || (tab.key !== "people" && tab.key !== "mcp")));
}

const LEVEL_LABEL: Record<Level, string> = {
  none: "No access",
  view: "View only",
  edit: "Can edit",
  manage: "Manage",
};

function ExternalIcon() {
  useLanguage();
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ width: 13, height: 13, flex: "none" }}>
      <path d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

function Tile({ tool }: { tool: GrantedTool }) {
  useLanguage();
  // External tools open in a new tab: they are a different origin (Teable is on
  // its own port, and in production has no public hostname at all), so replacing
  // the current tab would strand someone outside the platform with no way back.
  const external = tool.is_external;
  const contents = (
    <>
      <span className="tile__icon" aria-hidden="true">
        <ToolIcon name={tool.icon} />
      </span>
      <span className="tile__body">
        <span className="tile__name">
          {tool.is_external ? tool.name : translate(tool.name)}
          {external ? (
            <span className="tile__ext" title={translate("Opens in a new tab")}>
              <ExternalIcon />
            </span>
          ) : null}
        </span>
        {tool.description ? <span className="tile__desc">{tool.is_external ? tool.description : translate(tool.description)}</span> : null}
        {tool.is_coming_soon ? <span className="tile__coming-soon">{translate("Coming soon")}</span> : null}
        {/* Teable has no public IP in production, so the tile has to say that a
            tunnel is needed — otherwise it just looks broken. */}
        {tool.requires_tunnel ? (
          <span className="tile__warn">{translate("Needs an IAP tunnel to be open")}</span>
        ) : null}
      </span>
      <span className={`chip ${tool.level === "view" ? "" : "chip--accent"}`}>
        {translate(LEVEL_LABEL[tool.level])}
      </span>
    </>
  );

  // The upcoming modules are real permission targets, but no application exists
  // behind them yet. A non-link makes that state clear and prevents a dead route.
  if (tool.is_coming_soon) {
    return (
      <article className="tile tile--coming-soon" aria-label={`${tool.is_external ? tool.name : translate(tool.name)} — coming soon`}>
        {contents}
      </article>
    );
  }

  return (
    <a
      className="tile"
      href={tool.path}
      {...(external
        ? { target: "_blank", rel: "noreferrer noopener", "data-external": true }
        : {})}
    >
      {contents}
    </a>
  );
}

function NoTools() {
  useLanguage();
  const { me } = useAuth();
  return (
    <div className="card">
      <div className="empty">
        <div className="empty__title">{translate("No tools yet")}</div>
        <p style={{ maxWidth: 460, margin: 0 }}>
           {translate("Your account is set up, but nobody has given you access to anything yet. An administrator needs to grant you a tool before it appears here.")} </p>
        <p className="meta" style={{ marginTop: 4 }}>
           {translate("Signed in as")} {me.user.email}
        </p>
      </div>
    </div>
  );
}

export function App() {
  useLanguage();
  const me = useMe();
  const { config } = useAuth();
  // Signing in always lands on the Dashboard. It used to open on Profile for
  // anyone who had never saved one, which put a form in front of somebody who
  // came here to open the CRM — the first thing they saw was homework.
  //
  // The reason that existed has not gone away: a directory of unnamed email
  // addresses is nobody's idea of a People tab. The dot on the Profile tab now
  // carries that job alone, which is the right weight for it — a nudge beside
  // the thing it is about, not a gate in front of the work.
  const consentMode = new URLSearchParams(window.location.search).get("mcp_authorize") === "1";
  const [tab, setTab] = useState<Tab>(() => consentMode ? "mcp" : "apps");
  useEffect(() => {
    if (tab === "mcp" && !consentMode && !me.company) setTab("apps");
  }, [tab, consentMode, me.company]);

  // /me already filters to active, entitled tools; sorting here only guarantees
  // a stable order if the API's ordering ever changes.
  const tools = [...me.tools].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <AppShell>
      <PageHeader
        eyebrow={config.companyName}
        title={me.user.full_name ? translate("Welcome, {name}", {name:me.user.full_name.split(" ")[0]!}) : translate("Welcome")}
        actions={!consentMode ? (
          <div className="segmented" style={{ flexWrap: "wrap", maxWidth: "100%" }} role="tablist" aria-label={translate("Portal sections")}>
            {tabsFor(Boolean(me.company)).map(({ key, label }) => (
              <button
                key={key} role="tab" aria-selected={tab === key}
                className="viewtab" style={{ whiteSpace: "nowrap" }} data-active={tab === key}
                onClick={() => setTab(key)}
              >
                {translate(label)}
                {/* The only prompt left, now that signing in no longer opens
                    this tab. A `title` alone is a mouse-only affordance and is
                    not announced, so the dot carries a real label. */}
                {key === "profile" && !me.profile_complete ? (
                  <span className="dot-badge" role="img"
                        aria-label={translate("Your profile is not finished")}
                        title={translate("Finish your profile")} />
                ) : null}
              </button>
            ))}
          </div>
        ) : undefined}
      />

      {/* No company means the ACCOUNT HOME: the one screen about a person
          rather than a company. It lists the companies they can reach; each is
          a link to that company's own address, because the address is the
          tenancy boundary.

          `tools` here are PLATFORM-scoped — the vendor's own, which belong to
          the person rather than to any company, and which only a superadmin
          has. /me has always returned them and this screen never drew them, so
          the one person who administers the platform had no way to reach the
          console from the screen they land on.

          It is the same rule that has been broken four times in the other
          direction: displayed access must be computed by the same rule as
          enforced access. Granting something and never rendering it is the
          cheaper failure — nobody gets access they should not have — but it is
          the same disagreement. */}
      {tab === "apps" && !me.company ? (
        <>
          <CompanyPicker />
          {tools.length ? (
            <section style={{ marginTop: 30 }}>
              <h2 className="section-title">{translate("The platform")}</h2>
              <p className="muted" style={{ marginTop: 0, marginBottom: 14 }}>
                 {translate("Yours rather than any company's — which companies exist, what each one has, and who runs it.")} </p>
              <div className="tiles">
                {tools.map((tool) => (
                  <Tile key={tool.key} tool={tool} />
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {tab === "apps" && me.company ? (
        tools.length === 0 ? (
          <NoTools />
        ) : (
          <>
            <p className="muted" style={{ marginTop: -12, marginBottom: 20 }}>
              {translate(tools.length === 1 ? "One tool is available to you." : "{count} tools are available to you.", {count:tools.length})} </p>
            <div className="tiles">
              {tools.map((tool) => (
                <Tile key={tool.key} tool={tool} />
              ))}
            </div>
          </>
        )
      ) : null}

      {tab === "profile" ? <ProfileTab /> : null}
      {tab === "people" && me.company ? <PeopleTab companyName={me.company.name} /> : null}
      {consentMode ? <McpConnections consentMode companyId={me.company?.id} /> : tab === "mcp" && me.company ? <McpConnections key={me.company.id} companyId={me.company.id} /> : null}
    </AppShell>
  );
}
